import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  BackSide,
  Color,
  type Mesh,
  type ShaderMaterial,
} from 'three';
import { useNavigationStore } from '@/store/useNavigationStore';

/**
 * Warp tunnel — a long inside-rendered cylinder along the camera's
 * forward axis whose shader paints fast-moving light rings, plus a
 * cloud of small additive points that streak forward when the warp
 * is engaged. Activated only during 'warping' or 'returning'; in
 * idle/arrived it's invisible so it doesn't compete with the hero.
 */

const TUNNEL_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const TUNNEL_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform float uTime;
  uniform float uIntensity;
  uniform float uSpeed;
  uniform vec3 uColor;
  varying vec2 vUv;
  void main() {
    // Vertical UV runs along the cylinder length. We scroll a sequence
    // of bright rings using fract(uv*N - time*speed). 8 rings around
    // the tunnel, sharp falloff so the rings read as light bands.
    float u = vUv.y * 18.0 - uTime * uSpeed * 8.0;
    float ring = abs(fract(u) - 0.5) * 2.0;
    float band = pow(1.0 - ring, 8.0);

    // Soft radial fade from the open ends so the cylinder doesn't end
    // abruptly. vUv.y ∈ 0..1, end fades 0..0.15 and 0.85..1.
    float endFade = smoothstep(0.0, 0.18, vUv.y) * (1.0 - smoothstep(0.82, 1.0, vUv.y));

    float a = band * endFade * uIntensity;
    if (a < 0.005) discard;
    gl_FragColor = vec4(uColor * (0.6 + band * 0.8), a);
  }
`;

const STAR_VERTEX = /* glsl */ `
  uniform float uSpeed;
  uniform float uTime;
  attribute float aSize;
  attribute float aOffset;
  varying float vDepth;
  void main() {
    // Stars cycle along Z over time so they constantly stream past
    float zCycle = mod(position.z + uTime * uSpeed * 70.0 + aOffset * 200.0, 240.0) - 120.0;
    vec3 p = vec3(position.x, position.y, zCycle);
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vDepth = -mv.z;
    gl_PointSize = aSize * (180.0 / -mv.z) * (1.0 + uSpeed * 1.4);
    gl_Position = projectionMatrix * mv;
  }
`;

const STAR_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform float uIntensity;
  uniform vec3 uColor;
  varying float vDepth;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    // Elongated streak: stretch the visible disk along Y in the
    // PointCoord space (fragment-space anisotropy).
    float r = sqrt(c.x * c.x * 4.0 + c.y * c.y * 0.4);
    float disk = smoothstep(0.5, 0.0, r);
    float depthFade = smoothstep(120.0, 4.0, vDepth);
    float a = disk * depthFade * uIntensity;
    if (a < 0.005) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

const STAR_COUNT = 350;
const TUNNEL_RADIUS = 7;
const TUNNEL_LENGTH = 240;

function generateStars() {
  const positions = new Float32Array(STAR_COUNT * 3);
  const sizes = new Float32Array(STAR_COUNT);
  const offsets = new Float32Array(STAR_COUNT);
  for (let i = 0; i < STAR_COUNT; i++) {
    const theta = Math.random() * Math.PI * 2;
    // Bias toward outer ring so the corridor reads
    const r = Math.sqrt(0.45 + Math.random() * 0.55) * TUNNEL_RADIUS;
    positions[i * 3] = Math.cos(theta) * r;
    positions[i * 3 + 1] = Math.sin(theta) * r;
    positions[i * 3 + 2] = (Math.random() - 0.5) * TUNNEL_LENGTH;
    sizes[i] = 1.6 + Math.random() * 2.6;
    offsets[i] = Math.random();
  }
  return { positions, sizes, offsets };
}

export function WarpTunnel() {
  const phase = useNavigationStore((s) => s.phase);

  const tunnelMatRef = useRef<ShaderMaterial>(null);
  const starMatRef = useRef<ShaderMaterial>(null);
  const tunnelMeshRef = useRef<Mesh>(null);

  const tunnelUniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uIntensity: { value: 0 },
      uSpeed: { value: 0 },
      uColor: { value: new Color('#9bc7ff') },
    }),
    [],
  );
  const starUniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uIntensity: { value: 0 },
      uSpeed: { value: 0 },
      uColor: { value: new Color('#dceaff') },
    }),
    [],
  );

  const stars = useMemo(generateStars, []);

  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;

    // Target intensity / speed by phase
    let targetIntensity = 0;
    let targetSpeed = 0;
    if (phase === 'warping') {
      targetIntensity = 1;
      targetSpeed = 1;
    } else if (phase === 'returning') {
      targetIntensity = 0.55;
      targetSpeed = 0.65;
    } else if (phase === 'arrived') {
      targetIntensity = 0.18;
      targetSpeed = 0.12;
    }

    // Smooth approach (lerp at ~6/s)
    const lerp = 1 - Math.exp(-dt * 5);
    const tunU = tunnelUniforms;
    tunU.uTime.value = t;
    tunU.uIntensity.value += (targetIntensity - tunU.uIntensity.value) * lerp;
    tunU.uSpeed.value += (targetSpeed - tunU.uSpeed.value) * lerp;

    const starU = starUniforms;
    starU.uTime.value = t;
    starU.uIntensity.value += (targetIntensity - starU.uIntensity.value) * lerp;
    starU.uSpeed.value += (targetSpeed - starU.uSpeed.value) * lerp;
  });

  return (
    <group>
      {/* Cylindrical tunnel — rotated so its long axis aligns with -Z */}
      <mesh
        ref={tunnelMeshRef}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0, 0.5, -60]}
      >
        <cylinderGeometry args={[TUNNEL_RADIUS, TUNNEL_RADIUS, TUNNEL_LENGTH, 64, 1, true]} />
        <shaderMaterial
          ref={tunnelMatRef}
          vertexShader={TUNNEL_VERTEX}
          fragmentShader={TUNNEL_FRAGMENT}
          uniforms={tunnelUniforms}
          side={BackSide}
          transparent
          blending={AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Streaking stars cycling along the tunnel's z-axis */}
      <points position={[0, 0.5, -60]}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[stars.positions, 3]} />
          <bufferAttribute attach="attributes-aSize" args={[stars.sizes, 1]} />
          <bufferAttribute attach="attributes-aOffset" args={[stars.offsets, 1]} />
        </bufferGeometry>
        <shaderMaterial
          ref={starMatRef}
          vertexShader={STAR_VERTEX}
          fragmentShader={STAR_FRAGMENT}
          uniforms={starUniforms}
          transparent
          blending={AdditiveBlending}
          depthWrite={false}
        />
      </points>
    </group>
  );
}
